import type { AnyCodeDictionary } from '@mypoz/i18n';

/*
 * Package statuses used to live here as a two-entry stub (NEW, IN) — all the API document
 * enumerated, with a standing note to complete it from the live endpoint. MYP-40 did exactly
 * that, and replaced the stub rather than extending it: `package_status_list` publishes
 * twenty-two codes with a Chinese and an English label for each, which the packages provider now
 * fetches and `normalisePackages` resolves onto `Package.statusLabel`.
 *
 * Worth recording why the stub had to go rather than grow. It labelled `NEW` as "New", following
 * the document's own 新包裹. The live endpoint calls it 未抵达 / "No Arrival" — not a new package
 * but one that has not turned up yet, which is a different thing to tell someone waiting for it.
 * A hand-kept dictionary of someone else's workflow drifts from it; this one had, in the one
 * entry it was most confident about.
 */

/** `transtype` on a package or order. AIR is confirmed (空运); SEA and UNC appear as codes only. */
export const TRANSPORT_TYPE = [
  { code: 'AIR', labelKey: 'dict.transportType.air' }, // confirmed: 空运
  { code: 'SEA', labelKey: 'dict.transportType.sea' },
  { code: 'UNC', labelKey: 'dict.transportType.unc' },
] as const satisfies AnyCodeDictionary;

/*
 * Goods types used to live here too, as a hand-authored GENERAL/SENSITIVE pair. MYP-102 retired it
 * for the vendor's own `goods_type` endpoint, which the packages provider now fetches — the same
 * move MYP-40 made for statuses, for the same reason: the list is the vendor's to extend, and a
 * code this file did not know would have vanished from the filter instead of showing up raw.
 * The live endpoint currently publishes exactly the two this file carried.
 */

/*
 * Warehouse services used to live here as a label-only, 3-of-11 stub; MYP-34 replaced that with an
 * invented per-warehouse catalog, and MYP-100 replaced that with the vendor's own
 * `packages_services`, which the packages provider fetches (`servicesFor`).
 */

/**
 * `add_packages` refusal codes → what to tell the customer (MYP-98).
 *
 * 2, 3 and 4 are documented one by one. -2, -3 and -4 are documented only as one lumped sentence —
 * duplicate package, wrong status, or tracking number too long — without saying which code is
 * which, so all three share one honest message and the screen adds the vendor's own wording
 * underneath. Splitting them would mean guessing which is which; see the project rule on never
 * guessing a status code. A code not listed here renders as itself.
 */
export const ADD_PACKAGE_ERROR = [
  { code: '2', labelKey: 'packages.form.addError.failed' },
  { code: '3', labelKey: 'packages.form.addError.services' },
  { code: '4', labelKey: 'packages.form.addError.split' },
  { code: '-2', labelKey: 'packages.form.addError.refused' },
  { code: '-3', labelKey: 'packages.form.addError.refused' },
  { code: '-4', labelKey: 'packages.form.addError.refused' },
] as const satisfies AnyCodeDictionary;

/**
 * The same codes for an edit (MYP-123): `add_packages` is also how a package is updated, so the
 * codes are shared, but "couldn't be added" would be the wrong thing to tell someone saving a
 * change. -2/-3/-4 are documented as "duplicate, wrong status or number too long" — for an update
 * the likely one is a status the vendor does not allow editing at.
 */
export const EDIT_PACKAGE_ERROR = [
  { code: '2', labelKey: 'packages.form.editError.failed' },
  { code: '3', labelKey: 'packages.form.addError.services' },
  { code: '4', labelKey: 'packages.form.addError.split' },
  { code: '-2', labelKey: 'packages.form.editError.refused' },
  { code: '-3', labelKey: 'packages.form.editError.refused' },
  { code: '-4', labelKey: 'packages.form.editError.refused' },
] as const satisfies AnyCodeDictionary;

/**
 * Codes that are a clean refusal — the vendor said no and wrote nothing. Every other failure
 * (2 and 3, which may have half-written a package; and anything that stopped the answer arriving)
 * leaves it unknown whether the package now exists, so the list is re-read to find out.
 */
export const ADD_PACKAGE_CLEAN_REFUSALS: readonly string[] = ['4', '-2', '-3', '-4'];

/** The refusals the vendor words for itself — its own text is shown under the shared message. */
export const ADD_PACKAGE_SHOWS_VENDOR_TEXT: readonly string[] = ['-2', '-3', '-4'];

/**
 * A write failure where no answer arrived — network, timeout, an HTTP error, or a reply that could
 * not be read — so nobody knows whether the vendor carried the write out. Shared by Add (MYP-98)
 * and Delete (MYP-99), which both re-read the list and say so plainly in that case.
 */
export function isUnconfirmedFailure(code: string): boolean {
  return (
    code === 'NETWORK' ||
    code === 'TIMEOUT' ||
    code === 'EMPTY' ||
    code === 'MALFORMED' ||
    code.startsWith('HTTP_')
  );
}

/**
 * `delete_package` refusal codes (MYP-99). 0 and 2 are documented together as "delete failed, see
 * message", so they share one line with the vendor's own wording beneath; -2 is "non-POST or
 * illegal access". A code not listed here renders as itself.
 */
export const DELETE_PACKAGE_ERROR = [
  { code: '0', labelKey: 'packages.list.delete.error.refused' },
  { code: '2', labelKey: 'packages.list.delete.error.refused' },
  { code: '-2', labelKey: 'packages.list.delete.error.illegal' },
] as const satisfies AnyCodeDictionary;

/** Refusals after which nothing was deleted, so the list is left as it is. */
export const DELETE_PACKAGE_CLEAN_REFUSALS: readonly string[] = ['0', '2', '-2'];
